import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import {
  ArrowLeft, TrendingUp, CheckCircle, Lock, Play, Calendar,
  BarChart3, Users, Target, Lightbulb, Rocket, BookOpen,
  MessageSquare, DollarSign, Award, Clock, ChevronRight
} from "lucide-react";

const ENGAGEMENTS = [
  {
    id: "revenue_acceleration",
    title: "Revenue Acceleration Workshop",
    description: "Deep-dive session to identify and activate your top 3 revenue levers. Includes pricing optimization, upsell strategy, and sales funnel audit.",
    icon: DollarSign,
    color: "emerald",
    unlocks_at_month: 1,
    duration: "2 weeks",
    deliverables: ["Revenue lever analysis", "Pricing recommendation doc", "Upsell playbook", "30-day revenue action plan"],
    category: "Revenue"
  },
  {
    id: "brand_positioning",
    title: "Brand Positioning Sprint",
    description: "Clarify your market position, refine messaging, and create a brand story that converts. Includes competitor analysis and positioning statement.",
    icon: Target,
    color: "blue",
    unlocks_at_month: 1,
    duration: "1 week",
    deliverables: ["Positioning statement", "Competitor matrix", "Key messaging framework", "Elevator pitch script"],
    category: "Marketing"
  },
  {
    id: "ops_efficiency",
    title: "Operations Efficiency Audit",
    description: "Map your core processes, identify bottlenecks, and implement automation. Reduce time waste by 30% with systemized workflows.",
    icon: Clock,
    color: "amber",
    unlocks_at_month: 2,
    duration: "2 weeks",
    deliverables: ["Process map document", "Bottleneck report", "Automation recommendations", "SOP templates for top 5 processes"],
    category: "Operations"
  },
  {
    id: "team_scaling",
    title: "Team Scaling Blueprint",
    description: "Build a hiring roadmap, delegation framework, and team accountability system. Stop doing $20/hr tasks as a CEO.",
    icon: Users,
    color: "purple",
    unlocks_at_month: 2,
    duration: "2 weeks",
    deliverables: ["Hiring roadmap", "Role scorecards for next 3 hires", "Delegation matrix", "Weekly team cadence template"],
    category: "People"
  },
  {
    id: "customer_retention",
    title: "Customer Retention Engine",
    description: "Design a retention system that increases LTV by 40%. Includes loyalty program, NPS tracking, and re-engagement campaigns.",
    icon: Award,
    color: "rose",
    unlocks_at_month: 3,
    duration: "2 weeks",
    deliverables: ["Retention playbook", "Customer health scorecard", "Re-engagement email sequences", "Loyalty/referral program design"],
    category: "Revenue"
  },
  {
    id: "digital_presence",
    title: "Digital Presence Overhaul",
    description: "Audit and optimize your online footprint — website, social media, Google profile, and content strategy for lead generation.",
    icon: Rocket,
    color: "cyan",
    unlocks_at_month: 3,
    duration: "3 weeks",
    deliverables: ["Website audit & recommendations", "Social media content calendar", "Google Business optimization", "Lead magnet strategy"],
    category: "Marketing"
  },
  {
    id: "financial_modeling",
    title: "Financial Modeling & Forecasting",
    description: "Build a 12-month financial model with scenario planning. Understand your break-even, cash runway, and profit levers.",
    icon: BarChart3,
    color: "indigo",
    unlocks_at_month: 4,
    duration: "1 week",
    deliverables: ["12-month P&L forecast", "Break-even analysis", "Cash flow model", "3 growth scenario plans"],
    category: "Finance"
  },
  {
    id: "strategic_partnerships",
    title: "Strategic Partnerships Playbook",
    description: "Identify, approach, and close partnerships that multiply your reach. Includes partner scoring framework and outreach templates.",
    icon: MessageSquare,
    color: "teal",
    unlocks_at_month: 4,
    duration: "2 weeks",
    deliverables: ["Partner scoring matrix", "Outreach email templates", "Partnership agreement template", "Co-marketing campaign plan"],
    category: "Growth"
  },
  {
    id: "leadership_coaching",
    title: "CEO Leadership Intensive",
    description: "1-on-1 coaching sessions focused on executive presence, decision-making frameworks, and building a leadership rhythm.",
    icon: Lightbulb,
    color: "yellow",
    unlocks_at_month: 5,
    duration: "Ongoing",
    deliverables: ["Personal leadership assessment", "Decision-making framework", "Weekly CEO dashboard", "Quarterly strategic review template"],
    category: "Leadership"
  },
  {
    id: "exit_readiness",
    title: "Exit & Succession Readiness",
    description: "Whether you're building to sell or planning succession, this engagement gets your business exit-ready with proper documentation and valuation prep.",
    icon: BookOpen,
    color: "slate",
    unlocks_at_month: 6,
    duration: "3 weeks",
    deliverables: ["Business valuation framework", "Succession planning doc", "Operations manual compilation", "Investor-ready deck template"],
    category: "Strategy"
  }
];

const colorMap = {
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: "text-emerald-600", badge: "bg-emerald-100 text-emerald-800" },
  blue: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: "text-blue-600", badge: "bg-blue-100 text-blue-800" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", icon: "text-amber-600", badge: "bg-amber-100 text-amber-800" },
  purple: { bg: "bg-purple-50", border: "border-purple-200", text: "text-purple-700", icon: "text-purple-600", badge: "bg-purple-100 text-purple-800" },
  rose: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", icon: "text-rose-600", badge: "bg-rose-100 text-rose-800" },
  cyan: { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700", icon: "text-cyan-600", badge: "bg-cyan-100 text-cyan-800" },
  indigo: { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", icon: "text-indigo-600", badge: "bg-indigo-100 text-indigo-800" },
  teal: { bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-700", icon: "text-teal-600", badge: "bg-teal-100 text-teal-800" },
  yellow: { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-700", icon: "text-yellow-600", badge: "bg-yellow-100 text-yellow-800" },
  slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", icon: "text-slate-600", badge: "bg-slate-100 text-slate-800" },
};

export const EngagementsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [enrolledEngagements, setEnrolledEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const monthsInProgram = (() => {
    if (!user?.created_at) return 1;
    const start = new Date(user.created_at);
    const now = new Date();
    return Math.max(1, Math.ceil((now - start) / (30.44 * 24 * 60 * 60 * 1000)));
  })();

  // Tier-based max month: 90-day clients (growth_strategic) capped at month 3
  const userTier = user?.tier || 'basic';
  const is90DayClient = userTier === 'growth_strategic';
  const maxUnlockableMonth = is90DayClient ? 3 : 6;

  useEffect(() => {
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const res = await authFetch(`${API}/engagements`);
      if (res.ok) {
        const data = await res.json();
        setEnrolledEngagements(data);
      }
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };

  const enroll = async (engagementId) => {
    setEnrolling(engagementId);
    try {
      const res = await authFetch(`${API}/engagements/enroll`, {
        method: "POST",
        body: JSON.stringify({ engagement_id: engagementId })
      });
      if (res.ok) {
        const data = await res.json();
        setEnrolledEngagements(prev => [...prev, data]);
        toast.success("Enrolled successfully! Your consultant will be notified.");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to enroll");
      }
    } catch (_e) {
      toast.error("Enrollment failed");
    }
    setEnrolling(null);
  };

  const isEnrolled = (id) => enrolledEngagements.some(e => e.engagement_id === id);
  const getEnrollment = (id) => enrolledEngagements.find(e => e.engagement_id === id);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="engagements-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Button variant="ghost" className="mb-4 text-gray-600" onClick={() => navigate("/dashboard")}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-emerald-600" /> Growth Engagements
          </h1>
          <p className="text-gray-500 mt-1">
            {is90DayClient 
              ? "90-day program engagements. Upgrade to a 6-month plan to unlock advanced modules."
              : "Business-enhancing engagements that unlock throughout your 6-month program"
            }
          </p>
          <div className="flex items-center gap-3 mt-3">
            <Badge className="bg-emerald-100 text-emerald-800">
              Month {Math.min(monthsInProgram, maxUnlockableMonth)} of {maxUnlockableMonth === 3 ? 3 : 6}
            </Badge>
            <div className="flex-1 h-2 bg-gray-200 rounded-full max-w-xs">
              <div className="h-2 bg-emerald-500 rounded-full transition-all" style={{ width: `${Math.min(100, (monthsInProgram / maxUnlockableMonth) * 100)}%` }} />
            </div>
            <span className="text-xs text-gray-500">{enrolledEngagements.length}/{ENGAGEMENTS.length} enrolled</span>
          </div>
        </div>

        <div className="space-y-4">
          {ENGAGEMENTS.map((eng) => {
            const c = colorMap[eng.color] || colorMap.emerald;
            const Icon = eng.icon;
            const withinTierLimit = eng.unlocks_at_month <= maxUnlockableMonth;
            const unlocked = withinTierLimit && monthsInProgram >= eng.unlocks_at_month;
            const tierLocked = !withinTierLimit; // locked because tier doesn't include this
            const enrolled = isEnrolled(eng.id);
            const enrollment = getEnrollment(eng.id);
            const expanded = expandedId === eng.id;

            return (
              <Card
                key={eng.id}
                className={`transition-all ${unlocked ? `border ${c.border} hover:shadow-md cursor-pointer` : tierLocked ? "border border-amber-200 opacity-70" : "border border-gray-200 opacity-60"}`}
                onClick={() => unlocked && setExpandedId(expanded ? null : eng.id)}
                data-testid={`engagement-${eng.id}`}
              >
                <CardContent className="p-0">
                  <div className="flex items-center gap-4 p-4">
                    <div className={`w-12 h-12 rounded-xl ${unlocked ? c.bg : tierLocked ? "bg-amber-50" : "bg-gray-100"} flex items-center justify-center flex-shrink-0`}>
                      {unlocked ? <Icon className={`w-6 h-6 ${c.icon}`} /> : <Lock className={`w-5 h-5 ${tierLocked ? "text-amber-400" : "text-gray-400"}`} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className={`font-semibold ${unlocked ? "text-gray-900" : "text-gray-400"}`}>{eng.title}</h3>
                        <Badge variant="outline" className="text-xs">{eng.category}</Badge>
                        {enrolled && <Badge className="bg-emerald-100 text-emerald-800 text-xs">Enrolled</Badge>}
                        {tierLocked && (
                          <Badge className="bg-amber-100 text-amber-800 text-xs">6-Month Plan Required</Badge>
                        )}
                        {!unlocked && !tierLocked && <Badge variant="outline" className="text-xs text-gray-400">Unlocks Month {eng.unlocks_at_month}</Badge>}
                      </div>
                      <p className={`text-sm mt-0.5 ${unlocked ? "text-gray-600" : "text-gray-400"}`}>{eng.description}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {unlocked && <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expanded ? "rotate-90" : ""}`} />}
                    </div>
                  </div>

                  {expanded && unlocked && (
                    <div className={`border-t ${c.border} ${c.bg} p-4`}>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" /> Duration: {eng.duration}
                          </p>
                          <p className="text-xs font-semibold text-gray-600 mb-2">What You'll Get:</p>
                          <ul className="space-y-1.5">
                            {eng.deliverables.map((d, i) => (
                              <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                                <CheckCircle className={`w-3.5 h-3.5 ${c.icon} mt-0.5 flex-shrink-0`} />
                                {d}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="flex flex-col justify-end items-end">
                          {enrolled ? (
                            <div className="text-right">
                              <Badge className="bg-emerald-100 text-emerald-800 mb-2">
                                <CheckCircle className="w-3.5 h-3.5 mr-1" /> {enrollment?.status === "completed" ? "Completed" : "In Progress"}
                              </Badge>
                              {enrollment?.enrolled_at && (
                                <p className="text-xs text-gray-500">Enrolled: {new Date(enrollment.enrolled_at).toLocaleDateString()}</p>
                              )}
                            </div>
                          ) : (
                            <Button
                              onClick={(e) => { e.stopPropagation(); enroll(eng.id); }}
                              disabled={enrolling === eng.id}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              data-testid={`enroll-${eng.id}`}
                            >
                              {enrolling === eng.id ? (
                                <><Clock className="w-4 h-4 mr-1 animate-spin" /> Enrolling...</>
                              ) : (
                                <><Play className="w-4 h-4 mr-1" /> Start Engagement</>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EngagementsPage;
